// The share flow (solution sharing plan, stage 7, O20-O22): load the roles a solution can be shared
// with, choose, confirm, share, and the results, including "confirm again" (CONFIRM_AGAIN), Retry
// (RETRY) and a lost answer. The server applies every rule (4d/4e); this sends exactly what was
// confirmed. Used by the Solution Edit pop-up and the creation wizard's last screen.
import { useEffect, useRef, useState } from "react";
import { useLazyGetShareTargetsQuery, useShareSolutionMutation } from "services/roles";
import { shareConfirmation } from "utils/sharingSwitches";
import { isLostAnswer, lostAnswerReport } from "utils/shareTable";

const NOT_READY_WAIT_MS = 120000;
// Any 404 means sharing is not available: an older rolesMS whose unknown route answers an HTML page
// (RTK Query: PARSING_ERROR with originalStatus 404), or SHARING_OFF from a rolesMS before O19.
const isUnavailable = (error) =>
  !!error && (error.status === 404 || (error.status === "PARSING_ERROR" && error.originalStatus === 404));

// phase: loading | choose | confirm | sharing | result | unavailable | error | unknown
export function useShareFlow({ serviceId, retryNotReady, active }) {
  const [fetchTargets] = useLazyGetShareTargetsQuery();
  const [shareSolution] = useShareSolutionMutation();
  const [phase, setPhase] = useState("loading");
  const [message, setMessage] = useState("");
  const [targets, setTargets] = useState([]);
  const [notOffered, setNotOffered] = useState(null); // grant-all callers: the roles a rule excludes
  const [notOfferedCount, setNotOfferedCount] = useState(0); // finite callers: how many
  const [selected, setSelected] = useState([]);
  const [results, setResults] = useState([]);
  const [confirmAgain, setConfirmAgain] = useState(false);
  const [sending, setSending] = useState(0); // how many roles the running request sends
  // The lists the user confirmed, per role: Retry re-sends exactly these (never lists reloaded later).
  const confirmedRef = useRef({});
  // Stops the "still being set up" retry loop once the screen or pop-up goes.
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);
  const token = () => window.localStorage.getItem("token");
  const grantAll = notOffered !== null;

  async function load(keepSelected) {
    setPhase("loading");
    const started = Date.now();
    for (;;) {
      if (!aliveRef.current) return null;
      const res = await fetchTargets({ token: token(), serviceId }, false);
      if (res.isSuccess && res.data) {
        const list = res.data.payload || [];
        setTargets(list);
        setNotOffered(Array.isArray(res.data.notOffered) ? res.data.notOffered : null);
        setNotOfferedCount(res.data.notOfferedCount || 0);
        const ids = new Set(list.map((t) => String(t._id)));
        setSelected((prev) => (keepSelected ? prev.filter((id) => ids.has(id)) : []));
        return list;
      }
      const err = res.error || {};
      if (isUnavailable(err)) { setPhase("unavailable"); return null; }
      const code = err.data && err.data.errorCode;
      if (code === "SOLUTION_NOT_READY" && retryNotReady && Date.now() - started < NOT_READY_WAIT_MS) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        continue;
      }
      setMessage((err.data && err.data.message) || "The roles could not be loaded.");
      setPhase("error");
      return null;
    }
  }

  useEffect(() => {
    if (!active || !serviceId) return;
    aliveRef.current = true;
    confirmedRef.current = {};
    setResults([]);
    setConfirmAgain(false);
    load(false).then((list) => list && setPhase("choose"));
  }, [active, serviceId]);

  const mergeResults = (rows) =>
    // Keep every role's latest outcome across rounds (confirm again, retry), and every role written for
    // it in any round (administrator copies stay written even when the target was skipped).
    setResults((prev) => {
      const byId = new Map(prev.map((r) => [String(r.roleId), r]));
      rows.forEach((r) => {
        const old = byId.get(String(r.roleId));
        const written = new Map([...((old && old.written) || []), ...(r.written || [])].map((w) => [String(w._id), w]));
        byId.set(String(r.roleId), { ...r, written: [...written.values()] });
      });
      return [...byId.values()];
    });

  // `fromScreen`: the roles were just confirmed on screen (their lists are recorded); otherwise a
  // Retry, which re-sends the lists recorded when they were confirmed.
  async function doShare(roleIds, fromScreen) {
    setSending(roleIds.length);
    setPhase("sharing");
    if (fromScreen) Object.assign(confirmedRef.current, shareConfirmation(targets, roleIds).confirmed);
    const confirmed = {};
    roleIds.forEach((id) => { if (confirmedRef.current[id]) confirmed[id] = confirmedRef.current[id]; });
    const res = await shareSolution({ token: token(), serviceId, body: { roleIds, confirmed } });
    if (res.error) {
      if (isUnavailable(res.error)) { setPhase("unavailable"); return; }
      if (isLostAnswer(res.error)) {
        // The server may have written some or all of it: report what a fresh list shows.
        const again = await fetchTargets({ token: token(), serviceId }, false);
        if (again.isSuccess && again.data) {
          const chosen = targets.filter((t) => roleIds.includes(String(t._id)));
          mergeResults(lostAnswerReport(chosen, again.data, grantAll));
          setPhase("result");
          return;
        }
        setMessage("The outcome is unknown; reopen Share to check.");
        setPhase("unknown");
        return;
      }
      setMessage((res.error.data && res.error.data.message) || "Sharing did not complete.");
      setPhase("error");
      return;
    }
    const rows = (res.data && res.data.payload) || [];
    mergeResults(rows);
    if (rows.some((r) => r.errorCode === "CONFIRM_AGAIN")) {
      // The roles that would receive it, or the users lists, changed: show the new lists and ask again
      // for the roles that were not shared (what this round already did stays listed). If the list
      // cannot be reloaded or none of them is offered any more, show the results instead.
      const pendingIds = rows.filter((r) => r.errorCode === "CONFIRM_AGAIN").map((r) => String(r.roleId));
      const list = await load(true);
      const again = list ? pendingIds.filter((id) => list.some((t) => String(t._id) === id)) : [];
      if (again.length) {
        setSelected(again);
        setConfirmAgain(true);
        setPhase("confirm");
      } else {
        setPhase("result");
      }
      return;
    }
    setPhase("result");
  }

  return {
    phase,
    message,
    targets,
    notOffered,
    notOfferedCount,
    grantAll,
    selected,
    setSelected,
    results,
    confirmAgain,
    sending,
    retryIds: results.filter((r) => r.errorCode === "RETRY").map((r) => String(r.roleId)),
    openConfirm: () => { setConfirmAgain(false); setPhase("confirm"); },
    // In a "confirm again" round Cancel shows what was already done; otherwise back to the table with
    // the ticks kept.
    cancelConfirm: () => setPhase(confirmAgain ? "result" : "choose"),
    share: () => doShare(selected, true),
    retry: () => doShare(results.filter((r) => r.errorCode === "RETRY").map((r) => String(r.roleId)), false),
    reload: () => load(true).then((list) => list && setPhase("choose")),
    stop: () => { aliveRef.current = false; },
  };
}
